import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { BookOpenIcon } from "@heroicons/react/24/outline";
import { updateServiceAgreementAsync } from "../../../../slices/service/agreements";
import PMSwitch from "../../../utils/PMSwitch";
import { PMField } from "../../../utils/PMField";
import { FormikSaveBar } from "../../../utils/FormikSaveBar";
import { normalizeError } from "../../../../operationFeedback/normalizeError";
import { CustomerSelector } from "../../../utils/moduleSelectors/customerSelector";
import { ProjectSelector } from "../../../utils/moduleSelectors/projectSelector";
import { SystemSelector } from "../../../utils/moduleSelectors/systemSelector";
import { ServiceAgreementTypeSelector } from "../../../utils/moduleSelectors/serviceAgreementTypeSelector";
import { DeliveryAddressSelector } from "../../../utils/moduleSelectors/deliveryAddressSelector";
import { PromangRichTextEditor } from "../../../utils/PromangRichTextEditor";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import PMListbox from "../../../utils/PMListbox";
import { serviceAgreementIntervals } from "./utils";
import { ServiceAgreementContactsEditor } from "./agreement-contacts";
import { ServiceAgreementDiscountLinesEditor } from "./agreement-discount-lines";
import { ServiceAgreementSetupGuide } from "./setup-guide";
import { FieldHint, HintField, HintLabel } from "./field-hint";
import { isCompleteDeliveryAddress } from "../../../../constants/countries";
import {
  buildServiceAgreementFormInitialValues,
  buildServiceAgreementSavePayload,
  mergeAgreementFormItem,
} from "./agreementFormInitialValues";
import { AgreementFormErrorSummary } from "./AgreementFormErrorSummary";
import {
  buildAgreementContactsSchema,
  buildAgreementDiscountLinesSchema,
} from "./agreementNestedFormSchemas";

function FormSection({ title, description, hint, children }) {
  return (
    <section className="rounded-[18px] border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(0,0,0,.04)]">
      <h3 className="flex items-center text-[15px] font-extrabold tracking-tight text-slate-900">
        {title}
        <FieldHint content={hint} />
      </h3>
      {description ? (
        <p className="mt-1 text-sm text-slate-500">{description}</p>
      ) : null}
      <div className="mt-4 grid grid-cols-6 gap-5">{children}</div>
    </section>
  );
}

function FormikRichText({ name, formik, placeholder, size = "md" }) {
  return (
    <PromangRichTextEditor
      value={formik.values[name] || ""}
      onChange={(html) => {
        formik.setFieldValue(name, html);
        formik.setFieldTouched(name, true, false);
      }}
      placeholder={placeholder}
      size={size}
    />
  );
}

function seedContacts(item) {
  if (Array.isArray(item?.contacts) && item.contacts.length > 0) {
    return item.contacts;
  }
  if (item?.contact?.id) {
    return [
      {
        contact_id: item.contact.id,
        party: "customer",
        role: "",
        is_primary: true,
        contact: item.contact,
      },
    ];
  }
  return [];
}

/** Keeps parent enableReinitialize in sync with Formik dirty without render loops. */
function FormikDirtySync({ dirty, onDirtyChange }) {
  useEffect(() => {
    onDirtyChange(Boolean(dirty));
  }, [dirty, onDirtyChange]);
  return null;
}

export const ServiceAgreementForm = (props) => {
  const { t } = useTranslation();
  const { item, onRefresh, isLoading } = props;
  const dispatch = useDispatch();
  const saveGuard = useRef(false);
  const [feedback, setFeedback] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  // Avoid re-init from a partial/stale payload while the user has unsaved edits.
  const [formDirty, setFormDirty] = useState(false);
  const mergedItemRef = useRef(item);

  const mergedItem = useMemo(() => {
    if (formDirty) {
      return mergeAgreementFormItem(mergedItemRef.current, item);
    }
    const merged = mergeAgreementFormItem(mergedItemRef.current, item);
    mergedItemRef.current = merged;
    return merged;
  }, [item, formDirty]);

  const initialValues = useMemo(
    () => ({
      ...buildServiceAgreementFormInitialValues(mergedItem),
      contacts: seedContacts(mergedItem),
    }),
    [mergedItem]
  );

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <Formik
      enableReinitialize={!formDirty}
      initialValues={initialValues}
      validationSchema={Yup.object({
        order_date: Yup.date().required(t("service.agreements.form.order_date_required")),
        customer_id: Yup.string().required(t("service.agreements.form.customer_required")),
        agreement_type_id: Yup.string().required(t("service.agreements.form.type_required")),
        interval_id: item?.lifecycle_state && item.lifecycle_state !== "active" ? Yup.string().nullable() : Yup.string().required(t("service.agreement.validation.interval_required")),
        contacts: buildAgreementContactsSchema(t),
        discount_lines: buildAgreementDiscountLinesSchema(t),
      })}
      onSubmit={async (values, helpers) => {
        if (saveGuard.current || feedback?.unknown) return;
        saveGuard.current = true;
        setFeedback(null);
        let confirmed;
        try {
          const primary = (values.contacts || []).find(
            (row) => row.party === "customer" && row.is_primary && !row._destroy
          );
          const payload = buildServiceAgreementSavePayload(
            {
              ...values,
              contact_id: primary?.contact_id || values.contact_id,
            },
            mergedItem
          );
          confirmed = await dispatch(updateServiceAgreementAsync(payload)).unwrap();
          if (!confirmed?.id) throw new Error("Unconfirmed agreement save");
          mergedItemRef.current = mergeAgreementFormItem(mergedItemRef.current, confirmed);
          setFormDirty(false);
          helpers.resetForm({ values });
          setFeedback({ message: t("customerSettings.saved") });
        } catch (error) {
          const normalized = error?.safeMessage ? error : normalizeError(error);
          const unknown = normalized.outcome === "unknown" || !normalized.httpStatus || normalized.httpStatus >= 500;
          helpers.setErrors(Object.fromEntries((normalized.fieldErrors || []).map((row) => [row.path, row.message])));
          setFeedback({ error: true, unknown, message: unknown ? t("agreementWorkflow.unknown") : normalized.safeMessage });
        } finally {
          saveGuard.current = false;
          helpers.setSubmitting(false);
        }
        if (confirmed?.id) {
          try { await onRefresh?.(confirmed); }
          catch { setFeedback({ error: true, message: t("agreementWorkflow.updatedRefreshFailed") }); }
        }
      }}
    >
      {(formik) => (
        <>
        <FormikDirtySync dirty={formik.dirty} onDirtyChange={setFormDirty} />
        <Form className="space-y-4 pb-24">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setGuideOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
            >
              <BookOpenIcon className="h-4 w-4 text-[#1463C7]" />
              {t("service.agreements.guide.open")}
            </button>
          </div>

          <AgreementFormErrorSummary formik={formik} />
          {feedback && <p role={feedback.error ? "alert" : "status"} className="rounded-xl bg-slate-50 p-3 text-sm">{feedback.message}</p>}

          {!isCompleteDeliveryAddress(item?.delivery_address) ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {t("service.agreements.address.incomplete_banner")}
            </div>
          ) : null}

          <FormSection
            title={t("service.agreements.form.section_type")}
            description={t("service.agreements.form.section_type_help")}
            hint={t("service.agreements.form.section_type_hint")}
          >
            <div className="col-span-6 sm:col-span-3">
              <PMSwitch
                name="is_framework"
                label={
                  <HintLabel hint={t("service.agreements.form.is_framework_help")}>
                    {t("service.agreements.form.is_framework")}
                  </HintLabel>
                }
                {...formik}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <PMSwitch
                name="vat_included"
                label={
                  <HintLabel hint={t("service.agreements.form.vat_included_hint")}>
                    {t("service.agreements.form.vat_included")}
                  </HintLabel>
                }
                {...formik}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <ServiceAgreementTypeSelector
                selected={item?.type}
                {...formik}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <HintField hint={t("service.agreements.form.interval_hint")}>
                <PMListbox
                  label={t("service.agreements.info.interval")}
                  name="interval_id"
                  options={serviceAgreementIntervals}
                  selected={formik.values.interval_id}
                  {...formik}
                />
              </HintField>
            </div>
          </FormSection>

          <FormSection title={t("service.agreements.form.section_parties")}>
            <div className="col-span-6 sm:col-span-3">
              <PMSwitch
                name="invoice_to_project_customer"
                label={t("service.agreements.form.invoice_to_project")}
                {...formik}
              />
            </div>
            <div className="col-span-6 sm:col-span-3" />
            {!props.customer?.id && (
              <div className="col-span-6 sm:col-span-3">
                <CustomerSelector
                  label={t("service.agreements.form.customer")}
                  selected={item?.customer}
                  {...formik}
                />
              </div>
            )}
            <div className="col-span-6 sm:col-span-3">
              <CustomerSelector
                label={t("service.agreements.form.invoice_customer")}
                name="invoice_customer_id"
                selected={item?.invoice_customer}
                {...formik}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <ProjectSelector
                label={t("service.agreements.form.project")}
                selected={item?.project?.id ? item?.project : null}
                {...formik}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <SystemSelector
                label={t("service.agreements.form.system")}
                module="CUSTOMER"
                module_param={
                  item?.customer?.id
                    ? item?.customer?.id
                    : formik.values.customer_id
                }
                selected={item?.system}
                {...formik}
              />
            </div>
          </FormSection>

          <FormSection
            title={t("service.agreements.form.section_period")}
            description={t("service.agreements.form.section_period_help")}
          >
            <div className="col-span-6 sm:col-span-3">
              <PMField
                name="order_date"
                type="date"
                label={t("service.agreements.form.order_date")}
                {...formik}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <PMField
                name="agreement_start_date"
                type="date"
                label={t("service.agreements.form.start_date")}
                {...formik}
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <HintField hint={t("service.agreements.form.duration_years_hint")}>
                <PMField
                  name="duration_years"
                  type="number"
                  label={t("service.agreements.form.duration_years")}
                  {...formik}
                />
              </HintField>
            </div>
            <div className="col-span-6 sm:col-span-2">
              <HintField hint={t("service.agreements.form.notice_months_hint")}>
                <PMField
                  name="notice_months"
                  type="number"
                  label={t("service.agreements.form.notice_months")}
                  {...formik}
                />
              </HintField>
            </div>
            <div className="col-span-6 sm:col-span-2">
              <PMField
                name="period_end_date"
                type="date"
                label={t("service.agreements.form.period_end")}
                {...formik}
              />
            </div>
            <div className="col-span-6">
              <PMSwitch
                name="auto_renew"
                label={
                  <HintLabel hint={t("service.agreements.form.auto_renew_hint")}>
                    {t("service.agreements.form.auto_renew")}
                  </HintLabel>
                }
                {...formik}
              />
            </div>
          </FormSection>

          <FormSection
            title={t("service.agreements.form.section_discount")}
            description={t("service.agreements.form.section_discount_help")}
            hint={t("service.agreements.form.section_discount_hint")}
          >
            <div className="col-span-6 sm:col-span-2">
              <HintField hint={t("service.agreements.form.discount_percent_hint")}>
                <PMField
                  name="discount_percent"
                  type="number"
                  label={t("service.agreements.form.discount_percent")}
                  {...formik}
                />
              </HintField>
            </div>
            <div className="col-span-6 sm:col-span-2">
              <PMField
                name="discount_on_parts"
                type="number"
                label={t("service.agreements.form.discount_parts")}
                {...formik}
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <PMField
                name="discount_on_services"
                type="number"
                label={t("service.agreements.form.discount_services")}
                {...formik}
              />
            </div>
            <ServiceAgreementDiscountLinesEditor
              lines={formik.values.discount_lines || []}
              onChange={(next) => formik.setFieldValue("discount_lines", next)}
              formik={formik}
            />
          </FormSection>

          <FormSection
            title={t("service.agreements.form.section_prices")}
            description={t("service.agreements.form.section_prices_help")}
            hint={t("service.agreements.form.section_prices_hint")}
          >
            <p className="col-span-6 rounded-xl bg-blue-50 p-3 text-sm text-blue-900">
              {t("service.agreements.lines.form_pointer")}
            </p>
            <div className="col-span-6 sm:col-span-3">
              <HintField hint={t("service.agreements.form.offer_price_hint")}>
                <PMField
                  name="offer_price"
                  type="number"
                  label={t("service.agreements.form.offer_price")}
                  {...formik}
                />
              </HintField>
            </div>
            <div className="col-span-6 sm:col-span-3">
              <HintField hint={t("service.agreements.form.hourly_rate_hint")}>
                <PMField
                  name="hourly_rate"
                  type="number"
                  label={t("service.agreements.form.hourly_rate")}
                  {...formik}
                />
              </HintField>
            </div>
            <div className="col-span-6 sm:col-span-3">
              <HintField hint={t("service.agreements.form.callout_fee_hint")}>
                <PMField
                  name="callout_fee"
                  type="number"
                  label={t("service.agreements.form.callout_fee")}
                  {...formik}
                />
              </HintField>
            </div>
            <div className="col-span-6 sm:col-span-3">
              <HintField hint={t("service.agreements.form.parts_markup_hint")}>
                <PMField
                  name="parts_markup_percent"
                  type="number"
                  label={t("service.agreements.form.parts_markup")}
                  {...formik}
                />
              </HintField>
            </div>
            <div className="col-span-6 sm:col-span-3">
              <HintField hint={t("service.agreements.form.subcontractor_markup_hint")}>
                <PMField
                  name="subcontractor_markup_percent"
                  type="number"
                  label={t("service.agreements.form.subcontractor_markup")}
                  {...formik}
                />
              </HintField>
            </div>
          </FormSection>

          <FormSection
            title={t("service.agreements.form.section_address")}
            description={t("service.agreements.form.section_address_help")}
          >
            <div className="col-span-6">
              <DeliveryAddressSelector
                selected={item?.delivery_address}
                name="delivery_address_id"
                label={t("service.agreements.form.delivery_address")}
                {...formik}
              />
            </div>
          </FormSection>

          <FormSection
            title={t("service.agreements.form.section_contacts")}
            description={t("service.agreements.form.section_contacts_help")}
          >
            <div className="col-span-6">
              <ServiceAgreementContactsEditor
                contacts={formik.values.contacts || []}
                customerId={formik.values.customer_id}
                onChange={(next) => formik.setFieldValue("contacts", next)}
                formik={formik}
              />
            </div>
          </FormSection>

          <FormSection title={t("service.agreements.form.section_text")}>
            <div className="col-span-6">
              <PMField
                name="description"
                type="text"
                label={t("service.agreements.form.short_description")}
                multiline
                {...formik}
              />
            </div>
            <div className="col-span-6">
              <p className="mb-2 text-sm font-semibold text-slate-700">
                {t("service.agreements.form.agreement_text")}
              </p>
              <FormikRichText
                name="agreement_text"
                formik={formik}
                placeholder={t("service.agreements.form.agreement_text_placeholder")}
                size="lg"
              />
            </div>
            <div className="col-span-6">
              <p className="mb-2 text-sm font-semibold text-slate-700">
                {t("service.agreements.form.conditions")}
              </p>
              <FormikRichText
                name="conditions"
                formik={formik}
                placeholder={t("service.agreements.form.conditions_placeholder")}
                size="lg"
              />
            </div>
          </FormSection>

          <FormikSaveBar {...formik} disabled={formik.isSubmitting || feedback?.unknown || item?.lifecycle_state === "awaiting_signature"} />
        </Form>
        <ServiceAgreementSetupGuide
          open={guideOpen}
          onClose={() => setGuideOpen(false)}
        />
        </>
      )}
    </Formik>
  );
};
